import { createSelector, createSlice } from '@reduxjs/toolkit';
import type { Dictionary } from 'lodash';
import pick from 'lodash/pick';

import { isFromAddressBuildingTypeStorage } from '@backend/lib/lead/helperFunctions';
import {
  Lead,
  LeadInventory,
  LeadPhoto,
  LeadPhotoTag,
  LeadRoom,
} from '@backend/src/frontend-mongo-interfaces/mongoose.gen';
import { LeadBox } from '@backend/src/lib/lead';
import logger from 'logger';
import types from 'constants/actionTypes';
import { isLeadMultiSpace, itemsBySpace } from 'lib/space';
import {
  findLeadPhotoTagById,
  getLeadPhotoTagInfo,
  leadPhotoTagsBySpaceAndType,
  unconfirmedLeadPhotoTagsBySpaceAndType,
} from 'lib/photoTag';
import { initialLeadState as initialState } from 'lib/lead';
import { RootState } from 'store/configureStore';
import { LocationApiLocation } from 'lib/locations';

export const leadSlice = createSlice({
  name: 'lead',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(types.SAVE_LEAD_DETAILS, (state, action): Lead => {
        logger.info(types.SAVE_LEAD_DETAILS, action);
        return {
          ...state,
          ...action.detailsPartial,
        };
      })
      .addCase(
        types.SAVE_LEAD_SPACE,
        (
          state,
          {
            photo,
            newInventory,
          }: { photo: LeadPhoto; newInventory?: LeadInventory[] },
        ): Lead => {
          logger.info(types.SAVE_LEAD_SPACE, { photo, newInventory });
          const existingInventory = state.inventory || [];
          const inventory = [...existingInventory, ...(newInventory || [])];

          const existingPhotos = state.photos || [];
          const existingPhoto = existingPhotos.find((p) => p._id === photo._id);
          const photos = existingPhoto
            ? existingPhotos.map((p) => {
                return p._id === photo._id ? photo : p;
              })
            : [...existingPhotos, photo];

          return {
            ...state,
            photos,
            inventory,
          };
        },
      )
      .addCase(
        types.SAVE_LEAD_PHOTO,
        (state, { photo }: { photo: LeadPhoto }): Lead => {
          logger.info(types.SAVE_LEAD_PHOTO, photo);
          const existingPhotos = state.photos || [];
          const existingPhoto = existingPhotos.find((p) => p._id === photo._id);
          const photos = existingPhoto
            ? existingPhotos.map((p) => {
                return p._id === photo._id ? photo : p;
              })
            : [...existingPhotos, photo];

          return {
            ...state,
            photos,
          };
        },
      )
      // Shared
      .addCase(types.UPDATE_LEAD_SUCCESS, (state, action): Lead => {
        logger.info(types.UPDATE_LEAD_SUCCESS, action);
        return {
          ...state,
          ...(state.gdprConsent ? action.response.result : {}),
        };
      })
      // // Shared
      .addCase(types.AUTHENTICATE_RETURNING_SUCCESS, (_state, action): Lead => {
        return action.response.result.lead;
      })
      // Shared
      .addCase(types.AUTHENTICATE_RETURNING_FAILURE, () => {
        return initialState;
      })
      // Shared
      .addCase(types.RESET_LEAD, (): Lead => {
        return initialState;
      })
      // Shared
      .addCase(types.UPDATE_CURRENT_LOCATION, (state, action): Lead => {
        const currentLocation = action.payload as LocationApiLocation;
        return {
          ...state,
          locationID: parseInt(currentLocation?.id || '0', 10),
          franchiseID: currentLocation.franchise?.id,
        };
      });
  },
});

export default leadSlice;

export const selectLead = (state: RootState): Lead => state.lead;

export const selectLeadId = (state: RootState): Lead['_id'] =>
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (state.lead as Dictionary<any>).id ||
  state.lead._id ||
  state.app.activeLeadID;

export const selectLeadCompleteSteps = (
  state: RootState,
): Lead['completeSteps'] => state.lead?.completeSteps || [];

export const selectLeadVisibleSteps = (
  state: RootState,
): Lead['visibleSteps'] => state.lead?.visibleSteps || [];

export const selectLeadCurrentStep = (state: RootState): Lead['currentStep'] =>
  state.lead.currentStep;

export const selectLeadValidSteps = (state: RootState): Lead['validSteps'] =>
  state.lead?.validSteps || [];

export const selectIsStepComplete = createSelector(
  [selectLeadCompleteSteps, (_state, stepName) => stepName],
  (completeSteps, stepName) => completeSteps.includes(stepName),
);

const selectLeadMultiSpace = (state: RootState) =>
  pick(state.lead, ['oneSpace', 'inventory', 'rooms']);

export const selectIsLeadMultiSpace = createSelector(
  [selectLeadMultiSpace],
  (lead) => isLeadMultiSpace(lead),
);

const selectLeadFromAddressBuildingType = (state: RootState) =>
  pick(state.lead, ['customerType', 'fromAddress', 'toAddress']);

export const selectIsLeadStorage = createSelector(
  [selectLeadFromAddressBuildingType],
  (lead) => isFromAddressBuildingTypeStorage(lead),
);

export const selectLeadFormComplete = (
  state: RootState,
): Lead['formComplete'] => state.lead.formComplete;

export const selectLeadRoomsStart = (state: RootState): Lead['roomsStart'] =>
  state.lead.roomsStart;

export const selectLeadLocationId = (state: RootState): Lead['locationID'] =>
  state.lead.locationID;

export const selectLeadFranchiseId = (state: RootState): Lead['franchiseID'] =>
  state.lead.franchiseID;

export const selectLeadCustomerType = (
  state: RootState,
): Lead['customerType'] => state.lead.customerType;

export const selectLeadSelectedLocationDistance = (
  state: RootState,
): Lead['selectedLocationDistance'] => state.lead.selectedLocationDistance;

export const selectLeadServices = (state: RootState): Lead['services'] =>
  state.lead.services;

export const selectLeadInventory = (state: RootState): Lead['inventory'] =>
  state.lead.inventory;

export const selectLeadBoxes = (state: RootState): LeadBox[] =>
  state.lead.boxes;

export const selectLeadPhotos = (state: RootState): Lead['photos'] =>
  state.lead.photos;

export const selectLeadSpaces = (state: RootState): Lead['rooms'] =>
  state.lead.rooms;

export const selectLeadSpaceById = createSelector(
  [selectLeadSpaces, (_state, spaceId: string) => spaceId],
  (spaces, spaceId) => (spaces || []).find((space) => space._id === spaceId),
);

export const selectLeadSpaceIndex = createSelector(
  [selectLeadSpaces, (_state, spaceId: string) => spaceId],
  (spaces, spaceId) => {
    const spaceIds = (spaces || []).map((space) => space._id);
    return spaceIds.indexOf(spaceId) + 1;
  },
);

export const selectPhoto = createSelector(
  [selectLeadPhotos, (_state, id: string) => id],
  (photos, id) => (photos || []).find((photo) => photo._id === id),
);

export const selectLeadPhotosBySpaceId = createSelector(
  [selectLeadPhotos, (_state, spaceId: LeadRoom['_id']) => spaceId],
  (photos, spaceId) => itemsBySpace(spaceId, photos),
);

export const selectLeadBoxesBySpace = createSelector(
  [selectLeadBoxes, (_state, spaceId: LeadRoom['_id']) => spaceId],
  (boxes, spaceId) => itemsBySpace(spaceId, boxes),
);

export const selectLeadPhotoTag = createSelector(
  [
    selectLeadPhotos,
    (_state, photoId: LeadPhoto['_id']) => photoId,
    (_state, _photoId, tagId: LeadPhotoTag['_id']) => tagId,
  ],
  (leadPhotos, photoId, tagId) => {
    const photo = (leadPhotos || []).find((photo) => photo._id === photoId);
    return findLeadPhotoTagById({ tags: photo?.tags || [], tagId });
  },
);

export const selectLeadPhotoTagInfo = createSelector(
  [
    selectLeadPhotos,
    (_state, photoId: LeadPhoto['_id']) => photoId,
    (_state, _photoId, tagId: LeadPhotoTag['_id']) => tagId,
    (_state, _photoId, _tagId, unconfirmedOnly?: boolean) => unconfirmedOnly,
  ],
  (leadPhotos, photoId, tagId, unconfirmedOnly) => {
    const photo = (leadPhotos || []).find((photo) => photo._id === photoId);
    return photo
      ? getLeadPhotoTagInfo({ photo, tagId, unconfirmedOnly })
      : undefined;
  },
);

export const selectLeadPhotoTagsBySpaceAndType = createSelector(
  [
    selectLeadPhotos,
    (_state, spaceId: LeadRoom['_id']) => spaceId,
    (_state, _spaceId, itemType: number) => itemType,
  ],
  (leadPhotos, spaceId, itemType) =>
    leadPhotoTagsBySpaceAndType({
      leadPhotos,
      spaceId,
      itemType,
    }),
);

export const selectUnconfirmedLeadPhotoTagsBySpace = createSelector(
  [selectLeadPhotos, (_state, spaceId: LeadRoom['_id']) => spaceId],
  (leadPhotos, spaceId) =>
    unconfirmedLeadPhotoTagsBySpaceAndType({
      leadPhotos,
      spaceId,
    }),
);

export const selectLeadBoxBySpaceAndType = createSelector(
  [
    selectLeadBoxes,
    (_state, spaceId: LeadRoom['_id']) => spaceId,
    (_state, _spaceId, itemType: number) => itemType,
  ],
  (boxes, spaceId, itemType) =>
    boxes.find((box) => box.spaceId === spaceId && box.itemType === itemType),
);

export const selectLeadInventoryBySpace = createSelector(
  [selectLeadInventory, (_state, spaceId: LeadRoom['_id']) => spaceId],
  (inventory, spaceId) => itemsBySpace(spaceId, inventory),
);

export const selectLeadFinishValues = (
  state: RootState,
): Pick<Lead, 'roomsStart' | 'estimateID' | 'estimateCanGetPreviewLink'> =>
  pick(state.lead, ['roomsStart', 'estimateID', 'estimateCanGetPreviewLink']);
